'use client';

import React, { useEffect } from 'react';
import { Configure, UseHitsProps, useHits, useInstantSearch } from 'react-instantsearch';
import ProductCard from './product-card';
import { Product } from '@av/database';
import Loading from '@/components/loading/loading';
import { useSearchParams } from 'next/navigation';
import { Indices } from '@av/algolia';
import { getIndex } from '@av/algolia';
import { Paginator } from '@av/ui/pagination';

const HITS_PER_PAGE = 12;

const mapHitToProduct = (hit: any): Product =>
  ({
    ...hit,
    thumbnailImage: hit.shop_meta?.image_url || '',
    images: hit.shop_meta?.images,
    unitPrice: hit.specifications?.price || 0,
    taxRate: hit.specifications?.tax_rate || 0,
  }) as unknown as Product;

function PaginatedProducts(props: UseHitsProps) {
  const { items } = useHits(props);
  const { status, results } = useInstantSearch();
  const isLoading = status === 'loading' || status === 'stalled';
  const isComplete = status === 'idle';
  const hasResults = items.length > 0;

  if (!hasResults && isLoading) {
    return (
      <div className="relative m-auto flex min-h-[70vh] w-full items-center justify-center">
        <Loading size={60} className="w-full" />
      </div>
    );
  }

  const renderProducts = () => {
    if (!hasResults) {
      if (isComplete) {
        return (
          <div className="col-span-full py-8 text-center text-gray-500">
            Geen artikelen gevonden met de huidige filters.
          </div>
        );
      }

      return null;
    }

    return items.map((item) => <ProductCard key={item.objectID} product={mapHitToProduct(item)} />);
  };

  return (
    <div className="flex flex-col space-y-4 @container">
      <div className="grid grid-cols-1 gap-4 @2xl:grid-cols-2 @4xl:grid-cols-3">
        {renderProducts()}
      </div>

      {hasResults && <Paginator perPage={HITS_PER_PAGE} totalItems={results?.nbHits || 0} />}
    </div>
  );
}

const createPriceFilter = (min?: number, max?: number) => {
  if (min && max) return `price:${min} TO ${max}`;
  if (min) return `price >= ${min}`;
  if (max) return `price <= ${max}`;

  return '';
};

const createMaterialFilter = (specificationValues?: string) => {
  if (!specificationValues) return '';

  return specificationValues
    .split(',')
    .map((v) => `'specificationValues.${v.split(':')[0]}':'${v.split(':')[1]}'`)
    .join(' OR ');
};

const ProductHits = ({ filters }: { filters: string }) => {
  const searchParams = useSearchParams();
  const query = searchParams.get('query') || undefined;
  const min = searchParams.get('min') ? Number(searchParams.get('min')) * 100 : undefined;
  const max = searchParams.get('max') ? Number(searchParams.get('max')) * 100 : undefined;
  const page = searchParams.get('pagina') ? Number(searchParams.get('pagina')) : 1;
  const specificationValues = searchParams.get('specificaties') || undefined;

  const { refresh, setUiState } = useInstantSearch();

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setUiState((state) => {
        if (!state) return state;
        const index = state[getIndex(Indices.PRODUCTS)];
        if (index) {
          index.page = 0;
          index.query = query;
        }

        return state;
      });
      refresh();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [query, min, max, specificationValues, refresh, setUiState]);

  const refinedPrice = createPriceFilter(min, max);
  const refinedMaterial = createMaterialFilter(specificationValues);
  const facetFilters = [filters, refinedPrice, refinedMaterial].filter(Boolean).join(' AND ');

  return (
    <div className="w-full">
      <Configure
        hitsPerPage={HITS_PER_PAGE}
        page={page - 1}
        query={query}
        facetFilters={[]}
        filters={facetFilters}
      />
      <PaginatedProducts key={filters} />
    </div>
  );
};

export default ProductHits;
